import { Avatar, Box, Button, Input, Select } from "@mantine/core";
import {
  IconCalendar,
  IconCar,
  IconId,
  IconMail,
  IconPencil,
  IconPhone,
  IconUser,
} from "@tabler/icons-react";
import { Upload } from "antd";
import { useEffect, useState } from "react";

import { Input as AntInput } from "antd";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Address {
  city: string;
  district: string;
  postalCode: number;
  address: string;
}

interface EmployeeData {
  name: string;
  gender: string;
  birthday: string;
  bloodType: string;
  nationalIdNo: string;
  motorbikeLicense: boolean;
  carLicense: boolean;
  address: Address;
  user?: {
    email: string;
  };
  phone: string;
  img: string;
  emergencyContact: string;
  emergencyContactRelationship: string;
  emergencyContactPhoneNumber: string;
  profile: string | null;
  note: string;
}

interface ModalContent {
  title: string;
  onCancel: () => void;
  onOk: () => void;
}

function EditEmployeePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [employeeData, setEmployeeData] = useState<EmployeeData | null>(null);
  const [originalEmployeeData, setOriginalEmployeeData] =
    useState<EmployeeData | null>(null);
  const [editEmployeeData, setEditEmployeeData] = useState<
    Partial<EmployeeData>
  >({});
  const [profileImg, setProfileImg] = useState("");
  const [profileUrl, setProfileUrl] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState(false);
  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();

  const hasDataChanged = (): boolean => {
    // Check if any fields have been edited or profile image changed
    return Object.keys(editEmployeeData).length > 0 || profileUrl !== null;
  };

  const handleCancelButton = () => {
    const modalContent: ModalContent = {
      title: t("employee.edit.confirmCancel"),
      onCancel: contextCloseModal,
      onOk: () => {
        contextCloseModal();
        if (id) {
          navigate(`/hrMainPage/employee/${id}`);
        }
      },
    };
    contextOpenModal();
    contextSetModalContent(modalContent as any);
  };

  const handleChangeEmployeeData = (
    value: string | boolean | undefined,
    type: keyof EmployeeData
  ) => {
    setEmployeeData((prevData) => {
      if (!prevData) return null;
      return {
        ...prevData,
        [type]: value,
      };
    });

    // Only add to editEmployeeData if value is different from original
    if (originalEmployeeData && originalEmployeeData[type] !== value) {
      setEditEmployeeData((prevData) => ({
        ...prevData,
        [type]: value,
      }));
    } else {
      // Remove the field if it's back to original value
      setEditEmployeeData((prevData) => {
        const newData = { ...prevData };
        delete newData[type];
        return newData;
      });
    }
  };
  const handleChangeAddressData = (
    value: string | number,
    type: keyof Address
  ) => {
    const convertedValue =
      type === "postalCode" ? (value ? Number(value) : 0) : value;

    setEmployeeData((prevData) => {
      if (!prevData) return null;
      return {
        ...prevData,
        address: {
          ...prevData.address,
          [type]: convertedValue,
        },
      };
    });

    // Only add to editEmployeeData if address value is different from original
    if (
      originalEmployeeData &&
      originalEmployeeData.address?.[type] !== convertedValue
    ) {
      setEditEmployeeData((prevData) => ({
        ...prevData,
        address: {
          ...prevData.address,
          [type]: convertedValue,
        },
      }));
    } else {
      // Remove the field if it's back to original value
      setEditEmployeeData((prevData) => {
        const newData = { ...prevData };
        if (newData.address) {
          delete newData.address[type];
          // If address is empty, remove it entirely
          if (Object.keys(newData.address).length === 0) {
            delete newData.address;
          }
        }
        return newData;
      });
    }
  };

  const {
    name = "",
    gender = "",
    birthday = "",
    bloodType = "",
    nationalIdNo = "",
    motorbikeLicense = false,
    carLicense = false,
    address,
    user,
    phone = "",
    img = "",
    emergencyContact = "",
    emergencyContactRelationship = "",
    emergencyContactPhoneNumber = "",
    profile,
    note = "",
  } = employeeData || {};

  const {
    city = "",
    district = "",
    postalCode = 0,
    address: addressLine = "",
  } = address || {};

  const email = user?.email || "";

  const handleSubmitEdit = async ({
    id,
    editEmployeeData,
  }: {
    id: string;
    editEmployeeData: Partial<EmployeeData>;
  }) => {
    if (!id) return;

    let data: any = { ...editEmployeeData };

    if (profileUrl) {
      data.profile = profileUrl;
    }

    if (data.birthday && typeof data.birthday === "string") {
      data.birthday = new Date(data.birthday);
    }

    try {
      const result = await single(
        orpcClient.employee.update([
          {
            id: id!,
            ...data,
          },
        ])
      );

      if (result) {
        navigate("/hrMainPage/employees");
      }
    } catch (error) {
      console.error("employee.update_ERROR", error);
    }
  };

  const handleDisplayGender = (gender: string | undefined): string => {
    if (!gender) return t("employee.gender.unknown");
    if (gender === "male") {
      return t("employee.gender.male");
    } else if (gender === "female") {
      return t("employee.gender.female");
    } else {
      return t("employee.gender.unknown");
    }
  };

  const handleDisplayGenderENG = (gender: string | undefined): string => {
    if (!gender) return "unknown";
    if (gender === t("employee.gender.male")) {
      return "male";
    } else if (gender === t("employee.gender.female")) {
      return "female";
    } else {
      return "unknown";
    }
  };

  const handleBeforeUpload = async (file: File) => {
    if (uploadState) return false;
    setUploadState(true);
    try {
      const { blobSasUrl, blobUrl } = await orpcClient.storage.createBlobSasUrl({
        fileName: file.name,
        contentType: file.type,
      });

      await fetch(blobSasUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "x-ms-blob-type": "BlockBlob",
        },
      });

      setProfileImg(URL.createObjectURL(file));
      setProfileUrl(blobUrl);
    } catch (error) {
      console.error("upload error", error);
    } finally {
      setUploadState(false);
    }
    return false;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await single(orpcClient.employee.read([{ id: id! }]));
        setEmployeeData(result as any);
        setOriginalEmployeeData(result as any);
        setProfileImg((result as any)?.profile || "");
      } catch (error) {
        console.error(t("employee.edit.errorFetchingData"), error);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="flex h-full items-center justify-center p-8">
      <div className="w-[765px] rounded-xl bg-white p-14">
        <>
          <div className="flex justify-center">
            <Upload
              name="avatar"
              listType="picture-card"
              className="avatar-uploader"
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
            >
              {profileImg ? (
                <img
                  src={profileImg}
                  alt="avatar"
                  className="w-24 h-24 object-cover rounded-full"
                />
              ) : (
                <Avatar size="xl" key={name} name={name} />
              )}
            </Upload>
          </div>
          <div className="py-3 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("employee.edit.title")}
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.edit.name")}</div>
            <Input
              value={name || ""}
              onChange={(e) => handleChangeEmployeeData(e.target.value, "name")}
              placeholder="Input component"
              leftSection={<IconUser size={16} />}
            />
          </div>
          <div className="py-4">
            <div style={{ color: "#6F6F6F" }}>{t("employee.edit.gender")}</div>
            <Select
              value={handleDisplayGender(gender) || ""}
              data={[t("employee.gender.male"), t("employee.gender.female")]}
              onChange={(value) =>
                handleChangeEmployeeData(
                  handleDisplayGenderENG(value || ""),
                  "gender"
                )
              }
              placeholder="Input component"
              leftSection={<IconUser size={16} />}
            />
          </div>
          <div className="flex items-center justify-start">
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.birthday")}
              </div>
              <DatePicker
                value={birthday ? dayjs(birthday) : undefined}
                onChange={(e) =>
                  handleChangeEmployeeData(
                    e ? e.format("YYYY-MM-DD") : undefined,
                    "birthday"
                  )
                }
              />
            </div>

            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.bloodType")}
              </div>
              <Input
                value={bloodType || ""}
                placeholder="Input component"
                onChange={(e) =>
                  handleChangeEmployeeData(e.target.value, "bloodType")
                }
                leftSection={<IconId size={16} />}
              />
            </div>
            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.idNumber")}
              </div>
              <Input
                value={nationalIdNo || ""}
                placeholder="Input component"
                onChange={(e) =>
                  handleChangeEmployeeData(e.target.value, "nationalIdNo")
                }
                leftSection={<IconId size={16} />}
              />
            </div>
          </div>
          <div className="flex items-center justify-start">
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.licenses")}
              </div>
            </div>

            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.motorcycle")}
              </div>
              <Select
                value={
                  motorbikeLicense == true
                    ? t("employee.edit.has")
                    : t("employee.edit.hasNot")
                }
                data={[t("employee.edit.has"), t("employee.edit.hasNot")]}
                onChange={(value) =>
                  handleChangeEmployeeData(
                    value === t("employee.edit.has") ? true : false,
                    "motorbikeLicense"
                  )
                }
                placeholder="Input component"
                leftSection={<IconCar size={16} />}
              />
            </div>
            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>{t("employee.edit.car")}</div>
              <Select
                value={
                  carLicense == true
                    ? t("employee.edit.has")
                    : t("employee.edit.hasNot")
                }
                data={[t("employee.edit.has"), t("employee.edit.hasNot")]}
                onChange={(value) =>
                  handleChangeEmployeeData(
                    value === t("employee.edit.has") ? true : false,
                    "carLicense"
                  )
                }
                placeholder="Input component"
                leftSection={<IconCar size={16} />}
              />
            </div>
          </div>

          <div className="py-4 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("employee.edit.contactAddress")}
          </div>
          <div className="flex items-center justify-center">
            <div className="py-4">
              <div style={{ color: "#6F6F6F" }}>{t("employee.edit.city")}</div>
              <Input
                value={city || ""}
                placeholder="Input component"
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "city")
                }
                leftSection={<IconUser size={16} />}
              />
            </div>

            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.district")}
              </div>
              <Input
                value={district || ""}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "district")
                }
                placeholder="Input component"
                leftSection={<IconUser size={16} />}
              />
            </div>
            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.postalCode")}
              </div>
              <Input
                type="number"
                value={postalCode || ""}
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "postalCode")
                }
                placeholder="Input component"
                leftSection={<IconUser size={16} />}
              />
            </div>
            <div className="py-4 pl-2">
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.address")}
              </div>
              <Input
                value={addressLine || ""}
                placeholder="Input component"
                onChange={(e) =>
                  handleChangeAddressData(e.target.value, "address")
                }
                leftSection={<IconUser size={16} />}
              />
            </div>
          </div>

          <div className="py-4 text-2xl font-bold" style={{ color: "#6F6F6F" }}>
            {t("employee.edit.contactInfo")}
          </div>

          <div style={{ color: "#6F6F6F" }}>{t("employee.edit.email")}</div>
          <Input
            value={email || ""}
            placeholder="Input component"
            disabled={true}
            leftSection={<IconMail size={16} />}
          />
          <div className="flex items-center justify-start py-4">
            <div>
              <div style={{ color: "#6F6F6F" }}>
                {t("employee.edit.emergencyContact")}
              </div>
              <Input
                placeholder="Input component"
                leftSection={<IconUser size={16} />}
                value={emergencyContact || ""}
                onChange={(e) =>
                  handleChangeEmployeeData(e.target.value, "emergencyContact")
                }
              />
            </div>

            <div className="flex items-center justify-center">
              <div className="pl-2">
                <div style={{ color: "#6F6F6F" }}>
                  {t("employee.edit.relationship")}
                </div>
                <Input
                  leftSection={<IconUser size={16} />}
                  value={emergencyContactRelationship || ""}
                  onChange={(e) =>
                    handleChangeEmployeeData(
                      e.target.value,
                      "emergencyContactRelationship"
                    )
                  }
                />
              </div>

              <div className="pl-2">
                <div style={{ color: "#6F6F6F" }}>
                  {t("employee.edit.emergencyPhone")}
                </div>
                <Input
                  leftSection={<IconPhone size={16} />}
                  value={emergencyContactPhoneNumber || ""}
                  onChange={(e) =>
                    handleChangeEmployeeData(
                      e.target.value,
                      "emergencyContactPhoneNumber"
                    )
                  }
                />
              </div>
            </div>
          </div>

          <div className="py-4">
            <div className="text-2xl font-bold" style={{ color: "#6F6F6F" }}>
              {t("employee.edit.notes")}
            </div>
            <AntInput.TextArea
              placeholder="Input placeholder"
              value={note || ""}
              onChange={(e) => handleChangeEmployeeData(e.target.value, "note")}
            />
          </div>

          <div className="flex">
            <Button
              variant="default"
              style={{ width: "100%", margin: "1rem" }}
              radius="md"
              onClick={() => handleCancelButton()}
            >
              {t("employee.edit.cancel")}
            </Button>

            <Button
              variant="filled"
              color="gray"
              style={{ width: "100%", margin: "1rem" }}
              radius="md"
              disabled={!hasDataChanged()}
              onClick={() => handleSubmitEdit({ id: id!, editEmployeeData })}
            >
              {t("employee.edit.save")}
            </Button>
          </div>
        </>
      </div>
    </div>
  );
}
export default EditEmployeePage;
